import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';

import { ReactComponent as ScholarshipsImage } from '../../assets/images/scholarships-funds.svg';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import ImageButton from '../../components/ImageButton';
import InformationPanel from '../../components/InformationPanel';
import InputField from '../../components/InputField';
import Modal from '../../components/Modal';
import Select from '../../components/Select';
import { MAX_AMOUNT_LIMIT, MIN_AMOUNT_LIMIT } from '../../constants/numberLimit';
import { isNumber, isNumberNegative } from '../../lib/helpers';
import { currencyFormattedNumber } from '../../lib/numberFormatter';
import { periodTitle, requiresPrevious, requiresRemainder } from '../../lib/periodUtils';
import { periodPropTypes, otherFundsModalPropTypes } from '../../reducers/propTypes';
import { updateValue } from '../../utils/utils';

const ScholarshipsFundsModal = ({ onUpdateScholarship, scholarship, periods, currentPeriod }) => {
  const [showModal, setModal] = useState(false);
  const { previous, period, remainder } = scholarship;
  const [previousAmount, setPreviousAmount] = useState(previous.amount);
  const [periodAmount, setPeriodAmount] = useState(period.amount);
  const [remainderAmount, setRemainderAmount] = useState(remainder.amount);
  const { currency } = currentPeriod;
  const currencies = [{ value: currency, label: currency }];

  const openModal = () => setModal(true);
  const closeModal = () => setModal(false);

  const previousAmountValid = () => isNumber(previousAmount);
  const periodAmountValid = () => isNumber(periodAmount);
  const remainderAmountValid = () => isNumber(remainderAmount);

  const isInvalid = () => {
    if (
      isNumberNegative(previousAmount) ||
      isNumberNegative(periodAmount) ||
      isNumberNegative(remainderAmount)
    ) {
      return true;
    }

    if (requiresPrevious(periods, currentPeriod) && requiresRemainder(periods, currentPeriod)) {
      return !previousAmountValid() || !periodAmountValid() || !remainderAmountValid();
    }

    if (requiresPrevious(periods, currentPeriod)) {
      return !previousAmountValid() || !periodAmountValid();
    }

    if (requiresRemainder(periods, currentPeriod)) {
      return !periodAmountValid() || !remainderAmountValid();
    }

    return !periodAmountValid();
  };

  const addFunds = () => {
    onUpdateScholarship({
      previous: { currency, amount: previousAmount },
      period: { currency, amount: periodAmount },
      remainder: { currency, amount: remainderAmount },
    });

    closeModal();
  };

  const totalAmount = () => {
    if (previousAmountValid() && remainderAmountValid()) {
      return parseFloat(previousAmount) + parseFloat(periodAmount) + parseFloat(remainderAmount);
    }

    if (previousAmountValid()) {
      return parseFloat(previousAmount) + parseFloat(periodAmount);
    }

    if (remainderAmountValid()) {
      return parseFloat(periodAmount) + parseFloat(remainderAmount);
    }

    return parseFloat(periodAmount);
  };

  const formattedTotalAmount = () => {
    if (isInvalid() || totalAmount() === 0) {
      return null;
    }

    return currencyFormattedNumber(currency, totalAmount());
  };

  return (
    <Fragment>
      <ImageButton
        description="Confirmed scholarships"
        image={<ScholarshipsImage alt="scholarships" />}
        value={formattedTotalAmount()}
        handleClickEvent={openModal}
      />

      <Modal
        isOpen={showModal}
        title="What confirmed scholarships do you have?"
        onClose={closeModal}
      >
        {requiresPrevious(periods, currentPeriod) && (
          <div className="row-spacer">
            <p>How much have you already used for this course?</p>
            <CombinedTextField>
              <Select
                id="scholarship-previous-currency"
                value={currency}
                options={currencies}
                onChange={() => {}}
                disabled
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="scholarship-previous-amount"
                placeholder="Enter previous amount"
                value={previousAmount}
                onChange={e => updateValue(e, setPreviousAmount, MAX_AMOUNT_LIMIT)}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>
          </div>
        )}

        <div className="row-spacer">
          <p>{periodTitle(currentPeriod)}</p>
          <CombinedTextField>
            <Select
              id="scholarship-period-currency"
              value={currency}
              options={currencies}
              onChange={() => {}}
              disabled
            />

            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              id="scholarship-period-amount"
              placeholder="Enter period amount"
              value={periodAmount}
              onChange={e => updateValue(e, setPeriodAmount, MAX_AMOUNT_LIMIT)}
              min={MIN_AMOUNT_LIMIT}
              max={MAX_AMOUNT_LIMIT}
            />
          </CombinedTextField>
        </div>

        {requiresRemainder(periods, currentPeriod) && (
          <div className="row-spacer">
            <p>For the remainder of your course:</p>
            <CombinedTextField>
              <Select
                id="scholarship-remainder-currency"
                value={currency}
                options={currencies}
                onChange={() => {}}
                disabled
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="scholarship-remainder-amount"
                placeholder="Enter remainder amount"
                value={remainderAmount}
                onChange={e => updateValue(e, setRemainderAmount, MAX_AMOUNT_LIMIT)}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>
          </div>
        )}

        <div className="row-spacer">
          <Button type="button" isDisabled={isInvalid()} onClick={addFunds}>
            Add funds
          </Button>
        </div>

        <div className="row-spacer">
          <InformationPanel>
            <p className="no-margin-top no-margin-bottom">
              If you haven&apos;t been awarded a scholarship yet, you can update your application
              later.
            </p>
          </InformationPanel>
        </div>
      </Modal>
    </Fragment>
  );
};

ScholarshipsFundsModal.propTypes = {
  onUpdateScholarship: PropTypes.func.isRequired,
  scholarship: otherFundsModalPropTypes.isRequired,
  periods: PropTypes.arrayOf(periodPropTypes).isRequired,
  currentPeriod: periodPropTypes.isRequired,
};

export default ScholarshipsFundsModal;
